import { paneScrollElement } from '@/lib/split-view-scroll-sync';
import { isZyVersionNodeVisible, type SelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';

export function contentResourceGuidFromBlockAttrs(
    attrs: { guid?: string; id?: string } | undefined,
    sectionId: string | number | null | undefined,
): string | null {
    if (attrs?.guid) {
        return attrs.guid;
    }
    if (sectionId != null && sectionId !== '' && attrs?.id != null && attrs?.id !== '') {
        return `${sectionId}_${attrs.id}`;
    }
    return null;
}

/** Gap (px) between the sticky toolbar bottom and the reading line / scroll target. */
const READING_LINE_GAP = 2;

/** Gap (px) left between the sticky toolbar and the top of the scrolled-to block. */
const SCROLL_TARGET_TOP_GAP = 8;

/** Ignore candidates with no measurable layout (unmounted or `display: none`). */
const MIN_VISIBLE_HEIGHT = 1;

interface ContentResourceCandidate {
    pos: number;
    guid: string;
}

export interface MeasuredContentResourceCandidate {
    guid: string;
    top: number;
    bottom: number;
}

interface EditorViewportBand {
    viewTop: number;
    viewBottom: number;
    readingY: number;
}

/**
 * Scrollport that clips the editor (split pane inner scroll, or `.content-wrapper` in single-pane).
 */
function getEditorScrollContainer(editor: Editor): HTMLElement {
    return paneScrollElement(editor) ?? editor.view.dom.closest('.content-wrapper') ?? editor.view.dom;
}

/**
 * Visible editor band below the sticky TipTap toolbar through the bottom of the scrollport on screen.
 * Uses the scroll container's client rect, not the full ProseMirror document height.
 */
function getEditorViewportBand(previewEditor: Editor): EditorViewportBand {
    const scrollRect = getEditorScrollContainer(previewEditor).getBoundingClientRect();
    const toolbarBottom = document.querySelector('.tiptap-toolbar')?.getBoundingClientRect().bottom ?? scrollRect.top;
    const viewTop = toolbarBottom + READING_LINE_GAP;
    const viewBottom = scrollRect.bottom;

    const READING_LINE_RATIO = 0.35;
    const bandHeight = viewBottom - viewTop;
    const readingY = viewTop + bandHeight * READING_LINE_RATIO;

    return { viewTop, viewBottom, readingY };
}

/**
 * Height (px) of the sticky editor toolbar that overlays the top of the scroll container, so a
 * scrolled-to block can be offset to land just below it instead of hidden underneath.
 */
function getStickyToolbarOffset(): number {
    const toolbarHeight = document.querySelector('.tiptap-toolbar')?.getBoundingClientRect().height ?? 0;
    return toolbarHeight + SCROLL_TARGET_TOP_GAP;
}

/**
 * Given a position, returns the outermost DOM element of the top-level block (direct child of the
 * doc) that contains it. Blocks nested inside a `zyVersionBlock` resolve to the wrapper element, so
 * the whole activity is scrolled into view rather than an inner element mid-node.
 */
function getTopLevelBlockDOM(editor: Editor, posBeforeNode: number): HTMLElement | null {
    const doc = editor.state.doc;
    const $inside = doc.resolve(posBeforeNode + 1);
    const topLevelPos = $inside.depth >= 1 ? $inside.before(1) : posBeforeNode;
    const dom = editor.view.nodeDOM(topLevelPos);
    if (dom instanceof HTMLElement) {
        return dom;
    }
    // Fallback: resolve a DOM node inside the block and walk up to the nearest element.
    let node: Node | null = editor.view.domAtPos(posBeforeNode + 1).node;
    while (node && node.nodeType !== Node.ELEMENT_NODE) {
        node = node.parentElement;
    }
    return node as HTMLElement | null;
}

function getPreviewVersionFilter(previewEditor: Editor): SelectedZyVersionFilter {
    const storage = previewEditor.storage as { zyVersion?: { versionFilter?: SelectedZyVersionFilter } };
    return storage.zyVersion?.versionFilter ?? previewEditor.state.doc.attrs.selectedZyVersion ?? null;
}

/**
 * Returns false when the block sits inside a zyVersion wrapper hidden for the preview editor's
 * version filter (e.g. a split pane showing only one version).
 */
function isContentResourceVisibleInPreview(previewEditor: Editor, sourceEditor: Editor, pos: number): boolean {
    const $pos = sourceEditor.state.doc.resolve(pos + 1);
    const filter = getPreviewVersionFilter(previewEditor);

    for (let depth = $pos.depth; depth >= 1; depth--) {
        const node = $pos.node(depth);
        if (node.type.name === 'zyVersionBlock' || node.type.name === 'zyVersionInline') {
            if (!isZyVersionNodeVisible(filter, String(node.attrs.version ?? ''))) {
                return false;
            }
        }
    }
    return true;
}

/**
 * Content-resource candidates in document order, matching the preview payload: top-level doc
 * children, with inner blocks enumerated for each `zyVersionBlock` wrapper.
 */
function enumerateContentResourceCandidates(sourceEditor: Editor): ContentResourceCandidate[] {
    const doc = sourceEditor.state.doc;
    const sectionId = doc.attrs?.sectionId as string | number | null | undefined;
    const candidates: ContentResourceCandidate[] = [];

    const tryAdd = (node: PMNode, pos: number) => {
        const guid = contentResourceGuidFromBlockAttrs(node.attrs as { guid?: string; id?: string }, sectionId);
        if (guid) {
            candidates.push({ pos, guid });
        }
    };

    doc.forEach((node, offset) => {
        if (node.type.name === 'zyVersionBlock') {
            node.forEach((child, childOffset) => {
                tryAdd(child, offset + 1 + childOffset);
            });
            return;
        }
        tryAdd(node, offset);
    });

    return candidates;
}

function measureCandidateRect(previewEditor: Editor, pos: number): Pick<MeasuredContentResourceCandidate, 'top' | 'bottom'> | null {
    const element = getTopLevelBlockDOM(previewEditor, pos);
    if (!element) {
        return null;
    }
    const { top, height } = element.getBoundingClientRect();
    if (height < MIN_VISIBLE_HEIGHT) {
        return null;
    }
    return { top, bottom: top + height };
}

/**
 * Picks the content resource whose top edge is closest to the reading line of the viewport.
 * Mirrors {@link getViewportTopResourceGuid} in the preview iframe (section-from-message).
 */
export function pickContentResourceGuidNearestReadingLine(
    measured: MeasuredContentResourceCandidate[],
    readingY: number,
    viewTop: number,
    viewBottom: number,
): string | null {
    if (measured.length === 0) {
        return null;
    }

    // If a candidate fully intersects the viewport, prioritize it over candidates that are only partially visible.
    const intersecting = measured.filter(candidate => candidate.top >= viewTop && candidate.bottom <= viewBottom);
    const pool = intersecting.length > 0 ? intersecting : measured;

    let bestIndex = 0;
    let bestDistance = Math.abs(pool[0].top - readingY);

    for (let index = 1; index < pool.length; index++) {
        const distance = Math.abs(pool[index].top - readingY);
        if (distance < bestDistance) {
            bestIndex = index;
            bestDistance = distance;
        }
    }

    return pool[bestIndex].guid;
}

/**
 * Scroll the editor so the top-level block matching the content resource GUID is in view, offset to
 * sit just below the sticky toolbar. Matches {@link getContentResourceGuidAtViewportTop}
 * (attrs.guid, else composite sectionId_id).
 * @param sectionIdForComposite - When set (including `null`), used for `${sectionId}_${id}` matching instead of
 *   `editor.state.doc.attrs.sectionId`.
 */
export function scrollEditorToContentResourceGuid(editor: Editor, guid: string, sectionIdForComposite?: string | number | null): boolean {
    const doc = editor.state.doc;
    const sectionId =
        sectionIdForComposite !== undefined ? sectionIdForComposite : (doc.attrs?.sectionId as string | number | null | undefined);
    let found = false;
    // Descend the tree (not just direct doc children) so content blocks nested inside a
    // `zyVersionBlock` wrapper are matched. The preview payload flattens zyVersion wrappers, so the
    // GUID coming back from the preview belongs to the inner block, not the wrapper.
    doc.descendants((node, pos) => {
        if (found) {
            return false;
        }
        const candidate = contentResourceGuidFromBlockAttrs(node.attrs as { guid?: string; id?: string }, sectionId);
        if (candidate === guid) {
            found = true;
            const targetNode = getTopLevelBlockDOM(editor, pos);
            if (targetNode) {
                // `scroll-margin-top` keeps the block below the sticky toolbar and lets the browser
                // handle the nested scroll containers correctly.
                targetNode.style.scrollMarginTop = `${getStickyToolbarOffset()}px`;
                targetNode.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' });
            }
            return false;
        }
        return true;
    });
    return found;
}

/**
 * Content resource GUID for the block whose top edge is closest to the vertical 65% line of the
 * visible editor band — stays on a tall activity until its top is far above readingY, then advances
 * to the next activity when the user is near the end of the current one.
 * @param previewEditor - The preview editor instance (e.g. the split pane matching preview).
 * @param sourceEditor - The source editor instance (e.g. the main editor).
 * @returns {string|null} The content resource GUID or null if not found
 */
export function getContentResourceGuidAtViewportTop(previewEditor: Editor, sourceEditor: Editor): string | null {
    const { viewTop, viewBottom, readingY } = getEditorViewportBand(previewEditor);
    const candidates = enumerateContentResourceCandidates(sourceEditor);
    const measured: MeasuredContentResourceCandidate[] = [];

    for (const { pos, guid } of candidates) {
        if (!isContentResourceVisibleInPreview(previewEditor, sourceEditor, pos)) {
            continue;
        }
        const rect = measureCandidateRect(previewEditor, pos);
        if (rect == null) {
            continue;
        }
        measured.push({ guid, ...rect });
    }
    return pickContentResourceGuidNearestReadingLine(measured, readingY, viewTop, viewBottom);
}

export function snapToGuidIfRefSet(ref: { current: string | null }, snapToGuid: (guid: string) => void): void {
    const guid = ref.current;
    if (guid) {
        snapToGuid(guid);
    }
}
